// Ruta: frontend/src/core/hooks/useQRScanner.ts
import { useState, useCallback, useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';

interface UseQRScannerResult {
  scan: (elementId: string) => Promise<string>;
  isScanning: boolean;
  error: string | null;
  stopScanning: () => void;
}

export const useQRScanner = (): UseQRScannerResult => {
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scannerRef = useRef<Html5Qrcode | null>(null);

  const stopScanning = useCallback(() => {
    const stop = async () => {
      if (scannerRef.current) {
        try {
          await scannerRef.current.stop();
        } catch (_) {
          // Ignorar errores al detener
        }
        try {
          await scannerRef.current.clear();
        } catch (_) {
          // Ignorar errores al limpiar
        }
        scannerRef.current = null;
      }

      // 🔥 Mejora: intentar detener el stream manualmente (por si la recarga no ocurre)
      const videoElement = document.querySelector('video');
      if (videoElement && videoElement.srcObject) {
        const stream = videoElement.srcObject as MediaStream;
        stream.getTracks().forEach(track => track.stop());
        videoElement.srcObject = null;
      }

      setIsScanning(false);
    };
    stop();
  }, []);

  const scan = useCallback((elementId: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (!elementId) {
        reject(new Error('ID del elemento contenedor es requerido'));
        return;
      }

      // Si ya hay un escáner activo, detenerlo
      if (scannerRef.current) {
        stopScanning();
      }

      setIsScanning(true);
      setError(null);

      const html5QrCode = new Html5Qrcode(elementId);
      scannerRef.current = html5QrCode;

      html5QrCode.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (decodedText) => {
          console.log('✅ QR detectado:', decodedText); // <-- Agrega esto
          // No llamamos a stopScanning aquí porque lo hará la recarga forzada
          resolve(decodedText);
        },
        (errorMessage) => {
          console.warn('❌ Error de escaneo:', errorMessage); // <-- Cambia debug a warn
        }
      ).catch((err) => {
        setError(err.message);
        setIsScanning(false);
        reject(err);
      });
    });
  }, [stopScanning]);

  return { scan, isScanning, error, stopScanning };
};